// ClientRatingsPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Spinner,
  Alert,
  Badge,
  Button,
} from "react-bootstrap";
import {
  FaStar,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCheckCircle,
  FaTruck,
} from "react-icons/fa";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { userAPI } from "../../api/apiSystem";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import PaginationComponent from "../PaginationComponent";
import RatingModal from "../rating/RatingModal";
import DriverRatingDisplay from "../rating/DriverRatingDisplay";
import { sendRatingChatMessage } from "../../utils/ratingChat";

const shortPlace = (addr) => {
  if (!addr) return "—";
  if (Array.isArray(addr)) addr = addr[0] || "";
  const s = String(addr).trim();
  const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts.slice(0, 2).join(", ");
  return s.length > 32 ? s.slice(0, 32) + "…" : s;
};

const formatDate = (val) => {
  if (!val) return "—";
  try {
    // d.m.Y or ISO
    if (String(val).includes(".")) return String(val).split(" ")[0];
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString("de-DE");
  } catch {
    return String(val);
  }
};

export default function ClientRatingsPage() {
  const { t } = useTranslation();

  const [tab, setTab] = useState("unrated"); // unrated | rated
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
  });

  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showRatingDetailModal, setShowRatingDetailModal] = useState(false);

  const fetchList = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page, per_page: pagination.per_page };
        const res =
          tab === "unrated"
            ? await userAPI.transportsOrders.completedUnrated(params)
            : await userAPI.transportsOrders.completedRated(params);

        if (res?.success) {
          const list =
            res.data?.data?.data ||
            res.data?.data ||
            (Array.isArray(res.data) ? res.data : []);
          setItems(Array.isArray(list) ? list : []);
          setPagination(
            res.data?.meta ||
              res.meta || {
                current_page: page,
                last_page: 1,
                per_page: 15,
                total: list.length || 0,
              },
          );
        } else {
          setItems([]);
          setError(res?.message || t("rating.fetch_error", "Fehler beim Laden"));
        }
      } catch (err) {
        console.error(err);
        setItems([]);
        setError(t("rating.fetch_error", "Fehler beim Laden"));
      } finally {
        setLoading(false);
      }
    },
    [tab, pagination.per_page, t],
  );

  useEffect(() => {
    fetchList(1);
  }, [tab]); // eslint-disable-line

  const openRate = (order) => {
    setSelectedOrder(order);
    setShowRatingModal(true);
  };

  const openDetail = (order) => {
    setSelectedOrder(order);
    // setShowDetail(true);
    setShowRatingDetailModal(true)
  };

  const handleSubmitRating = async (payload) => {
    if (!selectedOrder) return;
    const transportId =
      selectedOrder.transport_id ||
      selectedOrder.transport?.id;
    const orderId = selectedOrder.id;

    if (!transportId || !orderId) {
      Swal.fire("Error", "Missing IDs", "error");
      return;
    }

    try {
      setSubmitting(true);
      const res = await userAPI.transportsOrders.rating(
        transportId,
        payload,
      );

      if (res?.success) {
        await sendRatingChatMessage({
          apiSendMessage: userAPI.transportOrderChat.sendMessage,
          transportId,
          orderId,
          item: selectedOrder.transport || selectedOrder,          // الـ transport details
          order: selectedOrder,          // الـ order
          scores: payload,
          direction: "customer_to_driver",
        });

        Swal.fire({
          icon: "success",
          title: t("rating.success_title", "Danke!"),
          text: t("rating.success_customer", "Bewertung gespeichert"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowRatingModal(false);
        setSelectedOrder(null);
        fetchList(pagination.current_page);
      } else {
        Swal.fire(
          "Error",
          res?.message || t("rating.error", "Fehler"),
          "error",
        );
      }
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || err.message || "Error",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const customerPerson = selectedOrder
    ? {
        name:
          selectedOrder.transport?.order?.driver_name ||
          selectedOrder.transport?.order?.firstname ||
          selectedOrder.customer_name ||
          t("rating.customer", "Kunde"),
        id:
          selectedOrder.transport?.order?.order_driver ||
          selectedOrder.transport?.order_id ||
          "—",
        avatar: null,
      }
    : null;

  const renderCard = (order) => {
    const tr = order.transport || {};
    const from = shortPlace(tr.booking_from);
    const to = shortPlace(tr.booking_to);
    const company =
      tr.user?.company_name ||
      tr.user?.user_name ||
      order.customer_name ||
      "—";
    const loadingDate = tr.loading_date || tr.loading_date_start;
    const unloadingDate = tr.unloading_date || tr.unloading_date_end;
    const rating = order.driverRating || order.driver_rating;

    return (
      <div
        key={order.id}
        className="mb-3 overflow-hidden"
        style={{
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        {/* Header bar */}
        <div
          className="px-3 py-2 d-flex justify-content-between align-items-center"
          style={{ background: tab === "unrated" ? "#f59e0b" : "#0d9488" }}
        >
          <div>
            <div className="fw-bold text-white" style={{ fontSize: 14 }}>
              {tab === "unrated"
                ? t("rating.in_progress", "Auftrag abgeschlossen")
                : t("rating.rated", "Bewertet")}
            </div>
            <div className="text-white" style={{ fontSize: 13, opacity: 0.95 }}>
              {company}
            </div>
          </div>
          <Badge bg="light" text="dark" pill>
            #{order.transport.booking_id || order.id}
          </Badge>
          {/* <Badge bg="light" text="dark" pill>
            #{order.order_id || order.id}
          </Badge> */}
        </div>

        <div className="p-3">
          {/* Route */}
          <div className="d-flex flex-column gap-2 mb-2">
            <div className="d-flex align-items-start gap-2">
              <span style={{ fontSize: 16 }}>📍</span>
              <div>
                <div className="fw-semibold" style={{ fontSize: 14 }}>
                  {from}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {formatDate(loadingDate)} {tr.loading_time || ""}
                </div>
              </div>
            </div>
            <div className="ps-2 text-muted">↓</div>
            <div className="d-flex align-items-start gap-2">
              <span style={{ fontSize: 16 }}>📍</span>
              <div>
                <div className="fw-semibold" style={{ fontSize: 14 }}>
                  {to}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {formatDate(unloadingDate)} {tr.unloading_time || ""}
                </div>
              </div>
            </div>
          </div>

          {tab === "unrated" ? (
            <>
              <div
                className="d-flex align-items-start gap-2 mb-3 p-2 rounded"
                style={{ background: "#f8fafc", fontSize: 12, color: "#64748b" }}
              >
                <FaInfoCircle className="mt-1 flex-shrink-0" />
                <span>
                  {t(
                    "rating.can_rate_after",
                    "Eine Bewertung kann nach Abschluss des Auftrags abgegeben werden",
                  )}
                  {unloadingDate ? ` (${formatDate(unloadingDate)}).` : "."}
                </span>
              </div>
              <Button
                className="w-100 fw-bold border-0"
                style={{ background: "#0d9f6e", borderRadius: 10 }}
                onClick={() => openRate(order)}
              >
                <FaStar className="me-2" />
                {t("rating.rate_customer", "Kunde bewerten")}
              </Button>
            </>
          ) : (
            <>
              {rating && (
                <div
                  className="d-flex align-items-center gap-2 mb-2 p-2 rounded"
                  style={{ background: "#ecfdf5" }}
                >
                  <FaCheckCircle color="#059669" />
                  <span className="fw-semibold" style={{ color: "#059669" }}>
                    {rating.average ?? rating.rating ?? "—"} / 5
                  </span>
                  <span className="text-muted small">
                    {rating.created_at || ""}
                  </span>
                </div>
              )}
              <Button
                variant="outline-secondary"
                className="w-100"
                style={{ borderRadius: 10 }}
                onClick={() => openDetail(order)}
              >
                {t("rating.view_rating", "Bewertung ansehen")}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <NavbarMenu />
      <div className="dark-table-container">
        <Container fluid>
          <div className="dark-table-card p-3 p-md-4">
            <h4 className="fw-bold mb-3" style={{ color: "#0f172a" }}>
              {t("driverCompleted.driverInfo.reviews", "Bewertungen")}
            </h4>

            {/* Tabs */}
            <div
              className="d-flex mb-3"
              style={{ borderBottom: "2px solid #e5e7eb" }}
            >
              <button
                type="button"
                className="flex-fill border-0 bg-transparent py-2 fw-bold"
                style={{
                  color: tab === "unrated" ? "#0d9488" : "#64748b",
                  borderBottom:
                    tab === "unrated"
                      ? "3px solid #0d9488"
                      : "3px solid transparent",
                  marginBottom: -2,
                }}
                onClick={() => setTab("unrated")}
              >
                {t("rating.tab_unrated", "ZUM AUSSTELLEN")}
              </button>
              <button
                type="button"
                className="flex-fill border-0 bg-transparent py-2 fw-bold"
                style={{
                  color: tab === "rated" ? "#0d9488" : "#64748b",
                  borderBottom:
                    tab === "rated"
                      ? "3px solid #0d9488"
                      : "3px solid transparent",
                  marginBottom: -2,
                }}
                onClick={() => setTab("rated")}
              >
                {t("rating.tab_rated", "AUSGESTELLT")}
              </button>
            </div>

            {error && (
              <Alert variant="danger" className="py-2">
                {error}
              </Alert>
            )}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#ff7b00" }} />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <FaStar size={36} style={{ opacity: 0.3 }} />
                <p className="mt-2 mb-0">
                  {tab === "unrated"
                    ? t("rating.no_unrated", "Keine offenen Bewertungen")
                    : t("rating.no_rated", "Noch keine abgegebenen Bewertungen")}
                </p>
              </div>
            ) : (
              items.map(renderCard)
            )}

            {pagination.total > 0 && (
              <PaginationComponent
                currentPage={pagination.current_page}
                lastPage={pagination.last_page}
                onPageChange={fetchList}
                disabled={loading}
              />
            )}
          </div>
        </Container>
      </div>

      {/* فورم التقييم */}
      <RatingModal
        show={showRatingModal}
        onHide={() => {
          setShowRatingModal(false);
          setSelectedOrder(null);
        }}
        role="driver"
        person={customerPerson}
        onSubmit={handleSubmitRating}
        submitting={submitting}
      />

      {/* تفاصيل تقييم موجود */}
      {/* ✅ Modal التقييم باستخدام DriverRatingDisplay */}
        {showRatingDetailModal && selectedOrder && (
        <div
            className="modal show d-block"
            style={{ background: "rgba(0,0,0,.5)", zIndex: 9999 }}
            onClick={() => {
            setShowRatingDetailModal(false);
            setSelectedOrder(null);
            }}
        >
            <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
            >
            <div className="modal-content" style={{ borderRadius: 16, border: "none" }}>
                {/* Header */}
                <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">
                    {t("driverCompleted.driverInfo.rating", "Ihre Bewertung")}
                </h5>
                <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                    setShowRatingDetailModal(false);
                    setSelectedOrder(null);
                    }}
                ></button>
                </div>

                {/* Body */}
                <div className="modal-body pt-2">
                {(() => {
                    const rating =
                    selectedOrder.driverRating ||
                    selectedOrder.driver_rating ||
                    null;

                    if (!rating) {
                    return (
                        <Alert variant="info" className="mb-0">
                        {t("rating.no_rating_found", "Keine Bewertung gefunden")}
                        </Alert>
                    );
                    }

                    return (
                    <DriverRatingDisplay
                        rating={rating}
                        comment={rating.comment || null}
                    />
                    );
                })()}
                </div>

                {/* Footer */}
                <div className="modal-footer border-0 pt-0">
                <Button
                    variant="secondary"
                    className="w-100"
                    style={{ borderRadius: 10 }}
                    onClick={() => {
                    setShowRatingDetailModal(false);
                    setSelectedOrder(null);
                    }}
                >
                    {t("common.close", "Schließen")}
                </Button>
                </div>
            </div>
            </div>
        </div>
        )}

      <Footer />
    </>
  );
}